import { buildPlayerPayload, dateOnly, idOf, validateTransfer } from './transferModel.mjs';

const rejectBeforeWrite = message => Object.assign(new Error(message), { outcome: 'not_saved' });
const entity = data => {
  const p = Array.isArray(data) && data.length === 1 ? data[0] : data;
  return p && !Array.isArray(p) && (p._id || p.id) ? p : null;
};
export const membershipFingerprint = player => JSON.stringify({
  provider: idOf(player.provider), discipline: idOf(player.discipline),
  teams: [...new Set((player.teams || []).map(idOf))].sort(),
  teamsSeason: (player.teamsSeason || []).map(t => ({
    id: idOf(t._id), team: idOf(t.team), start: dateOnly(t.startDate), end: dateOnly(t.endDate),
  })).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
});

// Même encodage arrays -> key[] et objet -> JSON que actions/players.js.
// On n'envoie ni champs peuplés ni photo vide.
export function normalizePlayerSearchFilters(filters = {}) {
  const normalized = { ...filters };
  if (Array.isArray(normalized.teams)) {
    const teamIds = normalized.teams.filter(Boolean).map(idOf);
    if (teamIds.length !== 1) throw new Error('Sélectionnez exactement une équipe pour rechercher les joueurs.');
    normalized.teams = teamIds[0];
  }
  return normalized;
}

export function playerFormData(player) {
  const form = new FormData();
  const fields = ['firstname', 'lastname', 'provider', 'discipline', 'number', 'birthday',
    'nationality', 'size', 'weight', 'positioning', 'photoName', 'thirdPartyId', 'thirdPartyEntityId', 'teams', 'teamsSeason'];
  fields.forEach(key => {
    if (player[key] === undefined) return;
    const value = player[key];
    if (Array.isArray(value)) value.forEach(item => form.append(`${key}[]`, typeof item === 'string' ? item : JSON.stringify(item)));
    else form.append(key, value ?? '');
  });
  return form;
}

export function createTransferApi({ api, onSaved = () => {} }) {
  // Cache de résultats par opération dans cette session ; aucune promesse
  // d'idempotence serveur : l'API fournie n'expose pas cette fonctionnalité.
  const completed = new Map();
  const inFlight = new Map();
  const uncertain = new Map();
  const readPlayer = async playerId => {
    const r = await api.fetchPlayerById(playerId);
    const p = entity(r.data);
    if (!p || idOf(p) !== idOf(playerId) || !Array.isArray(p.teamsSeason) || !Array.isArray(p.teams)) {
      throw new Error('La fiche complète du joueur est absente ou son format est inattendu.');
    }
    const resolveTeam = async value => {
      if (value && typeof value === 'object' && value.name) return value;
      const teamId = idOf(value);
      if (!teamId) throw new Error('Affiliation sans identifiant d’équipe.');
      const response = await api.fetchTeamById(teamId);
      const team = entity(response.data);
      if (!team || idOf(team) !== teamId) throw new Error('Impossible de charger une équipe du joueur.');
      return team;
    };
    const values = [...(p.teams || []), ...p.teamsSeason.map(t => t.team)];
    const unique = new Map(values.map(value => [idOf(value), value]));
    const teams = new Map(await Promise.all([...unique].map(async ([id, value]) => [id, await resolveTeam(value)])));
    return { ...p, teams: p.teams.map(t => teams.get(idOf(t))),
      teamsSeason: p.teamsSeason.map(t => ({ ...t, team: teams.get(idOf(t.team)) })) };
  };
  const publish = (command, player) => {
    const result = { status: 'saved', player };
    completed.set(command.operationId, result);
    uncertain.delete(command.operationId);
    // Une erreur de reducer ne doit pas faire croire à un échec de l'écriture.
    try { onSaved(player, command.isNew); } catch (error) { console.error('Redux : joueur enregistré mais store non actualisé.', error); }
    return result;
  };
  const execute = async command => {
    let payload;
    try {
      const snapshot = command.expectedPlayer;
      const player = command.isNew ? snapshot : await readPlayer(command.playerId);
      if (!command.isNew && membershipFingerprint(player) !== membershipFingerprint(snapshot)) {
        throw new Error('Les affiliations de ce joueur ont changé. Annulez cette ligne et rechargez le joueur.');
      }
      if (command.isNew) {
        const r = await api.checkExistingPlayer({ firstname: player.firstname || '', lastname: player.lastname,
          discipline: player.discipline, provider: player.provider });
        if (!Array.isArray(r.data)) throw new Error('Réponse de vérification des doublons inattendue.');
        if (r.data.length) throw new Error('Un joueur similaire existe : vérifiez la fiche avant de créer.');
      }
      const draft = { kind: command.kind, isNew: command.isNew, player,
        sourceTeam: command.sourceTeamId, destinationTeam: command.destinationTeamId,
        destinationUnknown: !!command.destinationUnknown, startDate: command.startDate };
      const context = { provider: command.player.provider, discipline: command.player.discipline,
        team: command.kind === 'departure' ? command.sourceTeamId : command.destinationTeamId };
      validateTransfer(draft, context);
      payload = buildPlayerPayload(draft, context);
    } catch (error) { throw rejectBeforeWrite(error.message || 'Impossible de vérifier le joueur avant enregistrement.'); }

    // Construire FormData avant la requête : une erreur locale n'a pas écrit.
    let form;
    try { form = playerFormData(payload); } catch (error) { throw rejectBeforeWrite(error.message); }
    try {
      const response = command.isNew ? await api.createPlayer(form, { force: false }) : await api.updatePlayer(command.playerId, form);
      if (response.status && (response.status < 200 || response.status >= 300)) throw new Error('Réponse d’enregistrement inattendue.');
      const returned = entity(response.data);
      const savedId = returned ? idOf(returned) : command.playerId;
      if (!savedId) throw new Error('Création reçue sans identifiant du joueur.');
      if (!command.isNew && savedId !== idOf(command.playerId)) throw new Error('Identifiant de réponse inattendu.');
      // Read-after-write : les relations renvoyées peuvent être de simples IDs.
      // La fiche relue sert à la fois à la vérification et à l'affichage.
      uncertain.set(command.operationId, { command, payload, playerId: savedId });
      const canonical = await readPlayer(savedId);
      if (!hasAppliedTransfer(canonical, command)) throw new Error('Le transfert n’est pas encore confirmé par la relecture.');
      return publish(command, canonical);
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.error?.message || error.message || 'Enregistrement interrompu.';
      // Seuls des refus HTTP explicites autorisent une nouvelle tentative.
      if (!uncertain.has(command.operationId) && [400, 401, 403, 404, 409, 422].includes(status)) throw rejectBeforeWrite(message);
      if (!uncertain.has(command.operationId)) uncertain.set(command.operationId, { command, payload, playerId: command.playerId });
      throw Object.assign(new Error(message), { outcome: 'unknown' });
    }
  };
  return {
    fetchPlayers: filters => api.fetchPlayers(normalizePlayerSearchFilters(filters)),
    fetchCompetitions: api.fetchCompetitions,
    fetchTeams: api.fetchTeams,
    checkExistingPlayer: api.checkExistingPlayer,
    fetchPlayerDetails: player => readPlayer(idOf(player)),
    saveTransfer(command) {
      if (completed.has(command.operationId)) return Promise.resolve(completed.get(command.operationId));
      if (inFlight.has(command.operationId)) return inFlight.get(command.operationId);
      if (uncertain.has(command.operationId)) return Promise.reject(Object.assign(new Error('Vérifiez le résultat incertain avant toute nouvelle tentative.'), { outcome: 'unknown' }));
      const promise = execute(command).finally(() => inFlight.delete(command.operationId));
      inFlight.set(command.operationId, promise);
      return promise;
    },
    async resolveTransfer(operationId) {
      if (completed.has(operationId)) return completed.get(operationId);
      const entry = uncertain.get(operationId);
      if (!entry) throw new Error('Opération absente de cette session.');
      if (!entry.playerId) throw new Error('Création sans identifiant reçu : vérifiez les joueurs et le traitement serveur avant de quitter cette page.');
      const player = await readPlayer(entry.playerId);
      if (!hasAppliedTransfer(player, entry.command)) throw new Error('Le transfert n’est pas confirmé. Aucune nouvelle écriture n’a été envoyée ; vérifiez le traitement serveur.');
      return publish(entry.command, player);
    },
  };
}

export function hasAppliedTransfer(player, command) {
  const expected = command.player.teamsSeason || [];
  const actual = player.teamsSeason || [];
  // Comparer chaque affiliation attendue, pas uniquement l'arrivée : la
  // fermeture de l'ancien club et la conservation de l'historique comptent.
  const signature = t => JSON.stringify([idOf(t.team), dateOnly(t.startDate), dateOnly(t.endDate)]);
  const counts = new Map();
  actual.forEach(t => counts.set(signature(t), (counts.get(signature(t)) || 0) + 1));
  return expected.length > 0 && expected.every(t => {
    const key = signature(t), remaining = counts.get(key) || 0;
    if (!remaining) return false;
    counts.set(key, remaining - 1);
    return true;
  }) && (command.player.teams || []).every(t => (player.teams || []).some(a => idOf(a) === idOf(t)));
}
